<template>
  <div>
    <v-row justify="center" class="mt-2">
      <v-col cols="10">
        <v-row>
          <v-col cols="12" md="6" align-self="center">
            <h2 class="text-left">
              Classroom Setup
            </h2>

            <h4 class="text-left">
              Create classrooms for your institution
            </h4>
          </v-col>

          <v-spacer></v-spacer>

          <v-col cols="12" md="3">
            <h2>
              2/4 Steps
            </h2>

            <v-progress-linear
              height="25"
              :value="50"
            >
            </v-progress-linear>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-card>
              <v-stepper v-model="stepperTab">
                <v-stepper-header>
                  <v-stepper-step
                    :complete="stepperTab > 1"
                    step="1"
                  >
                    Classroom Structure
                  </v-stepper-step>

                  <v-divider></v-divider>

                  <v-stepper-step
                    :complete="stepperTab > 2"
                    step="2"
                  >
                    Create Classrooms
                  </v-stepper-step>

                  <v-divider></v-divider>
                </v-stepper-header>
                
                <v-stepper-items>
                  <v-stepper-content step="1">
                    <v-form
                      ref="classroomSetupForm"
                      v-model="classroomSetupForm"
                      @submit.prevent
                    >
                      <v-container fluid class="mt-2">
                        <v-row class="my-0">
                          <v-col>
                            <h3 class="text-left font-weight-medium">
                              What best describes your classroom structure?
                            </h3>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col>
                            <v-radio-group
                              v-model="classroomStructure"
                              class="mx-1"
                            >
                              <v-radio
                                v-for="item in classroomTypes"
                                :key="item.id" 
                                class="my-2" 
                              >
                                <template v-slot:label class="">
                                  <div>
                                    <h3 class="d-inline-block font-weight-medium">{{ item.description }}</h3>
                                      <br>
                                    <h5 v-if="item.hint" class="d-inline-block font-weight-medium">{{ item.hint }}</h5>
                                  </div>
                                </template>
                              </v-radio>
                            </v-radio-group>
                          </v-col>
                        </v-row>

                        <v-row justify="center">
                          <v-col cols="4">
                            <v-btn
                              color="primary"
                              :disabled="isLoading || classroomStructure==null"
                              block
                              @click="saveClassroomStructure"
                            >
                              Save & Next
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-form>
                  </v-stepper-content>
                
                  <v-stepper-content step="2">
                    <div class="text-left mb-2">
                      <h3 class="font-weight-medium">Note:</h3>
                      <ul class="">
                        <li>Classrooms should be entered in proper 
                            <span class="text-decoration-underline">order</span>.
                            This order will be reflected everywhere.
                        </li>
                        <li>No rows can be left blank.</li>
                        <li>Classrooms must not be repeated.</li>
                        <li>Cross-verify the entered data before proceeding.</li>
                      </ul>
                    </div>

                    <!-- standard, division classroom -->
                    <v-form
                      ref="stdDivisionForm"
                      v-model="stdDivisionForm"
                      @submit.prevent
                      v-if="classroomStructure == 0"
                      class=""
                    >
                      <v-container fluid class="mt-2">
                        <v-row justify="center"
                          v-for="(item, i) in stdDivisionClassrooms"
                          :key="i"
                        >
                          <v-col cols="12" md="4" sm="4" align-self="center">
                            <v-combobox
                              outlined
                              dense
                              class="rounded-input mx-1"
                              v-model="item.standard"
                              :items="standardItems"
                              placeholder="eg. 1st"
                              label="Standard"
                              @change="classroomValidation(item, i); updateStandardItems();"
                              :rules="[rules.required]"
                              maxlength="25"
                              counter
                              :error-messages="errors.classroom.length && errors.classroom[i] ? errors.classroom[i].error : null"
                            ></v-combobox>
                          </v-col>
                          <v-col cols="12" md="4" sm="4" align-self="center">
                            <v-combobox
                              outlined
                              dense
                              class="rounded-input mx-1"
                              v-model="item.division"
                              :items="divisionItems"
                              label="Division"
                              placeholder="eg. A"
                              @change="classroomValidation(item, i); updateDivisionItems(stdDivisionClassrooms);"
                              :rules="[rules.required]"
                              maxlength="25"
                              counter
                              :error-messages="errors.classroom.length && errors.classroom[i] ? errors.classroom[i].error : null"
                            ></v-combobox>
                          </v-col>
                          <v-col cols="12" md="1" sm="1">
                            <v-icon
                              color="red"
                              v-if="i != 0"
                              @click="deleteClassroom(i, stdDivisionClassrooms)"
                              class="mt-2"
                            >
                              mdi-delete
                            </v-icon>
                          </v-col>
                        </v-row>

                        <v-row justify="center">
                          <v-col cols="4">
                            <v-btn
                              color="primary"
                              text
                              block
                              class="text-decoration-underline font-weight-medium"
                              @click="addClassroom"
                            >
                              + Add another classroom
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-form>

                    <!-- batch classroom -->
                    <v-form
                      ref="batchForm"
                      v-model="batchForm"
                      @submit.prevent
                      v-if="classroomStructure == 1"
                      class="mt-1"
                    >
                      <v-row 
                        justify="center"
                        v-for="(item, i) in batchClassrooms"
                        :key="i"
                      >
                        <v-col cols="12" md="4" sm="4" align-self="center">
                          <v-combobox
                            outlined
                            dense
                            class="rounded-input mx-1"
                            placeholder="eg. Jr. KG"
                            v-model="item.batch"
                            :items="batchItems"
                            label="Batch"
                            @change="classroomValidation(item, i); updateBatchItems();"
                            :rules="[rules.required]"
                            maxlength="25"
                            counter
                            :error-messages="errors.classroom.length && errors.classroom[i] ? errors.classroom[i].error : null"
                          ></v-combobox>
                        </v-col>
                        <v-col cols="12" md="1" sm="1">
                          <v-icon
                            color="red"
                            v-if="i != 0"
                            @click="deleteClassroom(i, batchClassrooms)"
                            class="mt-2"
                          >
                            mdi-delete
                          </v-icon>
                        </v-col>
                      </v-row>

                      <v-row justify="center">
                        <v-col cols="4">
                          <v-btn
                            color="primary"
                            text
                            block
                            class="text-decoration-underline font-weight-medium"
                            @click="addClassroom"
                          >
                            + Add another clasroom
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-form>

                    <!-- year, dept, division classroom -->
                    <v-form
                      ref="yearDeptDivisionForm"
                      v-model="yearDeptDivisionForm"
                      @submit.prevent
                      v-if="classroomStructure == 2"
                      class="mt-1"
                    >
                      <v-row 
                        justify="center"
                        v-for="(item, i) in yearDeptDivisionClassrooms"
                        :key="i"
                      >
                        <v-col cols="12" md="3" sm="3" align-self="center">
                          <v-combobox
                            outlined
                            dense
                            class="rounded-input mx-1"
                            v-model="item.year"
                            :items="yearItems"
                            placeholder="eg. FY"
                            label="Year"
                            @change="classroomValidation(item, i); updateYearItems();"
                            :rules="[rules.required]"
                            maxlength="25"
                            counter
                            :error-messages="errors.classroom.length && errors.classroom[i] ? errors.classroom[i].error : null"
                          ></v-combobox>
                        </v-col>
                        <v-col cols="12" md="3" sm="3" align-self="center">
                          <v-combobox
                            outlined
                            dense
                            class="rounded-input mx-1"
                            v-model="item.department"
                            :items="deptItems"
                            placeholder="eg. IT"
                            label="Department"
                            @change="classroomValidation(item, i); updateDeptItems();"
                            :rules="[rules.required]"
                            maxlength="25"
                            counter
                            :error-messages="errors.classroom.length && errors.classroom[i] ? errors.classroom[i].error : null"
                          ></v-combobox>
                        </v-col>
                        <v-col cols="12" md="3" sm="3" align-self="center">
                          <v-combobox
                            outlined
                            dense
                            class="rounded-input mx-1"
                            v-model="item.division"
                            :items="divisionItems"
                            placeholder="eg. A"
                            label="Division"
                            @change="classroomValidation(item, i); updateDivisionItems(yearDeptDivisionClassrooms);"
                            :rules="[rules.required]"
                            maxlength="25"
                            counter
                            :error-messages="errors.classroom.length && errors.classroom[i] ? errors.classroom[i].error : null"
                          ></v-combobox>
                        </v-col>
                        <v-col cols="12" md="1" sm="1">
                          <v-icon
                            color="red"
                            v-if="i != 0"
                            @click="deleteClassroom(i, yearDeptDivisionClassrooms)"
                            class="mt-2"
                          >
                            mdi-delete
                          </v-icon>
                        </v-col>
                      </v-row>

                      <v-row justify="center">
                        <v-col cols="4">
                          <v-btn
                            color="primary"
                            text
                            block
                            class="text-decoration-underline font-weight-medium"
                            @click="addClassroom"
                          >
                            + Add another clasroom
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-form>

                    <v-row justify="center">
                      <v-col align-self="center">
                        <v-btn
                          color="primary"
                          :disabled="isLoading || !(stdDivisionForm || batchForm || yearDeptDivisionForm) || redirecting"
                          @click="createClassrooms"
                        >
                          Save & Proceed
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-card>
          </v-col>
        </v-row>

        <v-row justify="center" class="my-1">
          <v-col align-self="center">
            <SetupVideoDemo
              title="Watch a complete setup video!"
              description="Learn how to setup an institution on Intract with just a few steps by watching this video"
              url="https://www.youtube.com/watch?v=UuTAWC1bOqM"
            />
          </v-col>
        </v-row>

        <v-row class="mb-1">
          <v-col>
            <v-card class="pa-3">
              <v-row>
                <v-col cols="12" md="8" align-self="center">
                  <h3 class="text-left">
                    Get in touch with our onboarding manager
                  </h3>
                </v-col>

                <v-spacer></v-spacer>

                <v-col cols="12" md="3" align-self="center">
                  <v-btn
                    color="primary"
                    @click="showSupportDialog = true"
                  >
                    Contact Support
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-dialog
			v-model="otherClassroomStructureDialog"
			persistent
			max-width="550px"
		>
			<v-card>
				<v-card-title class="justify-center grey--text text--darken-2 mb-2">
					<v-btn icon absolute top right class="font-weight-bold" color="dark" @click="otherClassroomStructureDialog = false">
						x
					</v-btn>
				</v-card-title>

        <v-card-text>
          <v-container fluid>
            <div class="mb-3">
              <h3 class="font-weight-medium">
                To create a customized classroom, you may contact our onboarding support
              </h3>
            </div>

            <div class="">
              <v-btn
                color="primary"
                @click="showSupportDialog = true"
              >
                Contact Support
              </v-btn>
            </div>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <SupportOptionsDialog
      :visible="showSupportDialog"
      @closeSupportOptionsDialog="closeSupportDialog"
    />

    <!-- snackbar -->
		<v-snackbar v-model="snackbar.visible" :color="snackbar.color" multi-line>
			{{ snackbar.message }}
			<template v-slot:action="{ attrs }">
				<v-btn dark text v-bind="attrs" @click="snackbar.visible = false">
					Close
				</v-btn>
			</template>
		</v-snackbar>
  </div>
</template>

<script>
import api from "@api/index"
// import authApi from "@api/auth/base"
import { mapActions, mapGetters } from "vuex"
import endpoints from "@api/repository"
// import Helper from "@utils/misc"
import moment from "moment"
import SupportOptionsDialog from "@components/widgets/dialogs/SupportOptionsDialog.vue"
import SetupVideoDemo from "@components/onboarding/SetupVideoDemo.vue"

export default {
  name: 'ClassroomSetup',

  components: {
    SupportOptionsDialog,
    SetupVideoDemo
  },

  props: {},

  computed: {
    ...mapGetters([
			"currentUser",
			"accessToken",
			"isLoading",
			"currentInstitution",
		]),

		apiEssentials() {
			return {
				accessToken: this.accessToken,
				setLoading: this.setLoading,
				handleErrorsFunction: this.handleApiErrors,
			};
		},
  },

  data() {
    return {
      moment: moment,
			snackbar: {
				visible: false,
				message: "",
				color: "dark",
			},

			rules: {
        required: (value) => !!value || "Required",
      },

      errors: {
        classroom: [],
      },

      showSupportDialog: false,
      stepperTab: null,
      classroomSetupForm: false,
      classroomStructure: null,

      classroomTypes: [
        {
          id: 1,
          description: 'Standard, Division (Eg: 1st A or 2nd A)',
          hint: 'Recommended for Schools',
        },
        {
          id: 2,
          description: 'Batch (Eg: Jr. KG or Sr. KG)',
          hint: 'Recommended for Preschools or Coaching Classes',
        },
        {
          id: 3,
          description: 'Year, Department, Division (Eg: FY IT A or TY EXTC B)',
          hint: 'Recommended for Colleges',
        },
        {
          id: 4,
          description: 'Other'
        }
      ],

      sectionsList: [],

      // form v-models
      stdDivisionForm: false,
      batchForm: false,
      yearDeptDivisionForm: false,

      // store items
      standardItems: [],
      divisionItems: [],
      batchItems: [],
      yearItems: [],
      deptItems: [],

      // v-model lists
      stdDivisionClassrooms: [
        {
          standard: '',
          division: ''
        },
      ],
      batchClassrooms: [
        {
          batch: '',
        }
      ],
      yearDeptDivisionClassrooms: [
        {
          year: '',
          department: '',
          division: '',
        }
      ],

      otherClassroomStructureDialog: false,

      sectionCategoryResponse: null,

      redirecting: false,
    }
  },

  methods: {
    ...mapActions(
      [
        "setLoading",
        "setTokens",
        "setCurrentUser",
        "setCurrentInstitution",
        "setFeeSessionData",
        "clearTokens"
      ]
    ),

    async handleApiErrors(err) {
      this.setLoading(false);
      console.log(err);
      if (err.response) {
        if (err.response.status == 409) {
          this.snackbar.message = err.response.data;
          this.snackbar.color = "error";
          this.snackbar.visible = true;
        }
      } else {
        this.snackbar.message = "Unable to process request";
        this.snackbar.color = "error";
        this.snackbar.visible = true;
      }
    },

    async updateCurrentInstitutionDetails() {
      if(this.currentInstitution){
        var response;
        response = await api.call(this.apiEssentials, 
                                endpoints.getInstitutionDetailsById + (this.currentInstitution ? this.currentInstitution.id : 0 )+
                                "/",
                              );

        if (response != null) {
          this.setCurrentInstitution(response);
        }
      }
    },

    async saveClassroomStructure() {
      if(this.classroomStructure!=null) { 
        if(this.classroomStructure == 3) {
          this.otherClassroomStructureDialog = true
        }
        else {
          if(this.classroomStructure == 0) {
            this.sectionsList = [1,2]
          }
          else if(this.classroomStructure == 1) {
            this.sectionsList = [3]
          }
          else if(this.classroomStructure == 2) {
            this.sectionsList = [4,5,2]
          }

          // api call to post section_category
          var response = await api.call(this.apiEssentials, 
                                    endpoints.createSectionCategory, 
                                    api.Methods.POST, 
                                    { category_list: this.sectionsList }
                                  )
          if(response) {
            this.updateCurrentInstitutionDetails()

            this.snackbar.message = "Classroom structured saved successfully!"
            this.snackbar.visible = true
            this.snackbar.color = "success"

            this.sectionCategoryResponse = response
            this.stepperTab = 2
          }
        }
      }
    },

    addClassroom() {
      if(this.classroomStructure == 0) {
        this.stdDivisionClassrooms.push({ standard: '', division: ''})
      }
      else if(this.classroomStructure == 1) {
        this.batchClassrooms.push({ batch: ''})
      }
      if(this.classroomStructure == 2) {
        this.yearDeptDivisionClassrooms.push({ year: '', department: '', division: ''})
      }
    },

    deleteClassroom(index, items) {
      this.errors.classroom[index] = null
      items.splice(index, 1)

      if(items == this.stdDivisionClassrooms) {
        this.updateStandardItems();
        this.updateDivisionItems(this.stdDivisionClassrooms);
      }
      else if(items == this.batchClassrooms) {
        this.updateBatchItems();
      }
      else if(items == this.yearDeptDivisionClassrooms) {
        this.updateYearItems();
        this.updateDeptItems();
        this.updateDivisionItems(this.yearDeptDivisionClassrooms);
      }
    },

    // UPDATE combobox dropdown values after TRIM and remove DUPLICATE values
    updateStandardItems() {
      this.standardItems = this.stdDivisionClassrooms.filter(c => c.standard != '')
      this.standardItems = this.standardItems.map(c => c.standard.trim())
      this.standardItems = [...new Set(this.standardItems)]
    },

    updateDivisionItems(currentList) {
      this.divisionItems = currentList.filter(c => c.division != '')
      this.divisionItems = this.divisionItems.map(c => c.division.trim())
      this.divisionItems = [...new Set(this.divisionItems)]
    },

    updateBatchItems() {
      this.batchItems = this.batchClassrooms.filter(c => c.batch != '')
      this.batchItems = this.batchItems.map(c => c.batch.trim())
      this.batchItems = [...new Set(this.batchItems)]
    },

    updateYearItems() {
      this.yearItems = this.yearDeptDivisionClassrooms.filter(c => c.year != '')
      this.yearItems = this.yearItems.map(c => c.year.trim())
      this.yearItems = [...new Set(this.yearItems)]
    },

    updateDeptItems() {
      this.deptItems = this.yearDeptDivisionClassrooms.filter(c => c.department != '')
      this.deptItems = this.deptItems.map(c => c.department.trim())
      this.deptItems = [...new Set(this.deptItems)]
    },

    // frontend check if no 2 or more classrooms are the same
    async classroomValidation(room, i) {
      var classroomValid = true
      var count = 0

      if(this.classroomStructure == 0) {
        this.stdDivisionClassrooms.forEach(s => {
          if(s.standard == room.standard && s.division == room.division) {
            count++
          }
        })
      }
      else if(this.classroomStructure == 1) {
        this.batchClassrooms.forEach(s => {
          if(s.batch == room.batch) {
            count++
          }
        })
      }
      else if(this.classroomStructure == 2) {
        this.yearDeptDivisionClassrooms.forEach(s => {
          if(s.year == room.year && s.department == room.department && s.division == room.division) {
            count++
          }
        })
      }
      
      if(count > 1) {
        classroomValid = false
      }

      if(!classroomValid) {
        var errObj = { error: 'Classroom already exists' }
        this.$set(this.errors.classroom, i, errObj)
      }
      else {
        this.$set(this.errors.classroom, i, null)
      }
    },

    async createClassrooms() {
      // run validations
      if(this.classroomStructure == 0) {
        if(!this.$refs.stdDivisionForm.validate()) 
          return;
      }
      else if(this.classroomStructure == 1) {
        if(!this.$refs.batchForm.validate()) 
          return;
      }
      else if(this.classroomStructure == 2) {
        if(!this.$refs.yearDeptDivisionForm.validate()) 
          return;
      }

      var post_body = {
        section_category_ids: this.sectionCategoryResponse
      }

      if(this.classroomStructure == 0) {
        post_body.classrooms = this.stdDivisionClassrooms
      }
      else if(this.classroomStructure == 1) {
        post_body.classrooms = this.batchClassrooms
      }
      else if(this.classroomStructure == 2) {
        post_body.classrooms = this.yearDeptDivisionClassrooms
      }

      // trim classroom values
      post_body.classrooms.map(c => {
        Object.keys(c).forEach(key => {
          c[key] = c[key].trim()
          return c[key]
        })
      })
     
      var response = await api.call(this.apiEssentials, endpoints.createClassrooms, api.Methods.POST, post_body)

      if(response) {
        this.updateCurrentInstitutionDetails()
        this.redirecting = true

        this.snackbar.message = "Classrooms have been saved! REDIRECTING to the next stage..."
        this.snackbar.visible = true
        this.snackbar.color = "success"


        var self = this
        setTimeout(() => {
          self.$router.push({ name: 'StudentsSetup' })
        }, 2000)
      }
    },

    closeSupportDialog() {
      this.showSupportDialog = false
    }
  },

  created() {
    if(this.currentInstitution) {
      if(this.currentInstitution.stage == 0) {
        this.$router.push({ name: 'Dashboard' })
      }
      else if(this.currentInstitution.stage == 1) {
        this.$router.push({ name: 'ModulesSetup' })
      }
      else if(this.currentInstitution.stage == 3) {
        this.$router.push({ name: 'StudentsSetup' })
      }
      else if(this.currentInstitution.stage == 4) {
        this.$router.push({ name: 'StaffSetup' })
      }
    }
    else {
      this.$router.push({ name: 'LoginInstitute' })
    }
  }
}
</script>

<style>

</style>